import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { checkHubSession } from '@alascorporation/api-client'
import { PasswordInput } from '@alascorporation/ui'
import { api, storeSession } from '../lib/api'
import { env } from '../config/env'
import { AuthShell } from '../components/AuthShell'

type LoginMode = 'account' | 'group'

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
  ) {
    return (error as { response: { data: { error: string } } }).response.data.error
  }
  return fallback
}

export function LoginPage() {
  const [mode, setMode] = useState<LoginMode>('account')
  const [email, setEmail] = useState('')
  const [loginCode, setLoginCode] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [checkingSession, setCheckingSession] = useState(true)
  const [showPasswordHelp, setShowPasswordHelp] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const hasSsoRequest = searchParams.has('client_id') && searchParams.has('redirect_uri')
  const nextPath = hasSsoRequest ? `/authorize?${searchParams.toString()}` : '/'

  function switchMode(next: LoginMode) {
    setMode(next)
    setError('')
    setShowPasswordHelp(false)
  }

  // A user who already has a live session on some other *.satelab.org app
  // carries the shared cookie here too — skip the form entirely, the same
  // way arriving at accounts.google.com already signed in skips it there.
  // Irrelevant to group mode: a shared group account has no hub session.
  useEffect(() => {
    let cancelled = false
    checkHubSession(env.apiUrl).then((result) => {
      if (cancelled) return
      if (result) {
        storeSession(result.user, result.token)
        navigate(nextPath, { replace: true })
      } else {
        setCheckingSession(false)
      }
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleAccountSubmit() {
    const { data } = await api.post('/auth/login', { email, password })
    storeSession(data.data.user, data.data.token)
    navigate(nextPath, { replace: true })
  }

  // A shared group account has no individual identity and no hub
  // session/cookie — it goes straight through the one-shot SSO
  // authorize+token handoff instead of the two-step login-then-/authorize
  // dance the account flow uses (see AuthorizePage).
  async function handleGroupSubmit() {
    const { data } = await api.post('/auth/sso/group-authorize', {
      login_code: loginCode.trim(),
      password,
      client_id: searchParams.get('client_id'),
      redirect_uri: searchParams.get('redirect_uri'),
      code_challenge: searchParams.get('code_challenge') ?? undefined,
      state: searchParams.get('state') ?? undefined,
    })
    window.location.href = data.data.redirect
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // The submit button disables while isLoading, but that only blocks a
    // mouse click on it — pressing Enter inside the form calls this handler
    // directly and would otherwise fire a second concurrent request.
    if (isLoading) return
    setError('')
    setIsLoading(true)
    try {
      if (mode === 'group') {
        await handleGroupSubmit()
      } else {
        await handleAccountSubmit()
      }
    } catch (err) {
      setError(getApiErrorMessage(err, mode === 'group' ? 'Código o contraseña incorrectos' : 'Credenciales incorrectas'))
      setIsLoading(false)
    }
  }

  if (checkingSession) {
    return (
      <AuthShell>
        <div className="auth-card">
          <p className="auth-eyebrow">SateLab</p>
          <h1 className="auth-title">Iniciar sesión</h1>
          <div className="auth-loading">
            <span className="auth-spinner" aria-hidden="true" />
            Verificando tu sesión...
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <p className="auth-eyebrow">SateLab</p>
        <h1 className="auth-title">Iniciar sesión</h1>
        <p className="auth-subtitle">
          {mode === 'group'
            ? 'Usa el código de acceso y la contraseña de tu equipo o grupo.'
            : 'Una sola cuenta para acceder a todos los servicios del ecosistema.'}
        </p>

        <div className="auth-mode-switch" role="tablist" aria-label="Tipo de acceso">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'account'}
            className={`auth-mode-switch__option${mode === 'account' ? ' auth-mode-switch__option--active' : ''}`}
            onClick={() => switchMode('account')}
          >
            Cuenta individual
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'group'}
            className={`auth-mode-switch__option${mode === 'group' ? ' auth-mode-switch__option--active' : ''}`}
            onClick={() => switchMode('group')}
          >
            Equipo o grupo
          </button>
        </div>

        {error ? (
          <div className="auth-error" role="alert">
            {error}
          </div>
        ) : null}

        {mode === 'group' && !hasSsoRequest ? (
          <div className="auth-notice" role="status">
            El login de equipos y semilleros solo funciona al llegar desde un enlace de una
            app del ecosistema (Academy, CCCD, TRA) — esa app es la que sabe a dónde
            enviarte después. Abre este formulario desde ahí para continuar, o{' '}
            <button type="button" className="auth-link-button" onClick={() => switchMode('account')}>
              vuelve a iniciar sesión con tu cuenta individual
            </button>
            .
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            {mode === 'group' ? (
              <>
                <label className="auth-label" htmlFor="login_code">
                  Código de acceso
                </label>
                <input
                  id="login_code"
                  type="text"
                  required
                  autoFocus
                  autoComplete="off"
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  placeholder="semillero-xyz-2026"
                  className="auth-input"
                />
              </>
            ) : (
              <>
                <label className="auth-label" htmlFor="email">
                  Correo
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="auth-input"
                />
              </>
            )}

            <div className="auth-label-row">
              <label className="auth-label" htmlFor="password">
                Contraseña
              </label>
              {mode === 'account' ? (
                <button
                  type="button"
                  className="auth-inline-link"
                  onClick={() => setShowPasswordHelp((v) => !v)}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              ) : null}
            </div>
            <PasswordInput
              id="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              className="auth-input"
            />

            {mode === 'account' && showPasswordHelp ? (
              <div className="auth-notice" role="status">
                Por ahora la contraseña se restablece con ayuda de un administrador o
                instructor de tu plataforma (Academy, Gestión, CCCD, TRA) — pídeles un
                restablecimiento y usa la nueva contraseña temporal para entrar aquí.
              </div>
            ) : null}

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? 'Ingresando...' : 'Entrar'}
            </button>
          </form>
        )}

        {mode === 'account' ? (
          <p className="auth-footer">
            No tienes cuenta?{' '}
            <Link to={`/register?${searchParams.toString()}`}>Crear cuenta</Link>
          </p>
        ) : null}
      </div>
    </AuthShell>
  )
}
